import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Database, GitBranch, Loader2 } from "lucide-react";
import { toast } from "sonner";

import {
  DatasourceEditor,
  ModelDatasourceBuilder,
} from "@/components/portal/apps/reporting/datasources";
import { usePortalData } from "@/components/portal/PortalDataProvider";
import { useModelAccess } from "@/hooks/useModelAccess";
import { usePermissions } from "@/hooks/usePermissions";
import { cn } from "@/lib/utils";

export const Route = createFileRoute(
  "/portal/apps/reporting/datasources/new",
)({
  component: NewDatasourcePage,
});

type CreateMode = "blank" | "model";

/** The reporting service's own refusal text (dp `_require_creator.py`
 * `CREATOR_REQUIRED_DETAIL`), so the page and the API say the same thing. */
const DATASOURCE_CREATOR_REQUIRED =
  "Only organisation owners and admins can add data sources.";

function NewDatasourcePage() {
  const { can, rbacResolving } = usePermissions();
  const navigate = useNavigate();
  // An org_member never creates a data source (Steve, 2026-10-15). The list
  // hides "New datasource" behind the same `manage` check (CP grants it to
  // org_owner / org_admin / platform_admin only); this catches a typed or
  // bookmarked URL. Held until RBAC settles: `can` denies before then, and an
  // admin must not be bounced by the pre-fetch default.
  const canManageReporting = can("manage", "reporting");
  const mayCreate = !rbacResolving && canManageReporting;
  useEffect(() => {
    if (rbacResolving || canManageReporting) return;
    toast.error(DATASOURCE_CREATOR_REQUIRED);
    void navigate({ to: "/portal/apps/reporting/datasources", replace: true });
  }, [rbacResolving, canManageReporting, navigate]);

  if (!mayCreate) {
    return (
      <div className="flex items-center gap-2 p-6 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        Checking access…
      </div>
    );
  }
  return <NewDatasourceForm />;
}

function NewDatasourceForm() {
  const { selectedOrganisation } = usePortalData();
  const organisationId = selectedOrganisation?.organisationId ?? "";
  const { allowed: modelAllowed } = useModelAccess();
  const [mode, setMode] = useState<CreateMode>("blank");

  if (mode === "model") {
    return <ModelDatasourceBuilder />;
  }

  return (
    <div className="space-y-6 max-w-5xl mx-auto">
      {/* Fail-closed: this entry point only renders when the access probe
          (GET /model-tables) allows it. */}
      {modelAllowed && (
        <div className="grid grid-cols-2 gap-4 max-w-md">
          <ModeCard
            icon={Database}
            title="Blank SQL"
            description="Write your own query"
            active={mode === "blank"}
            onClick={() => setMode("blank")}
          />
          <ModeCard
            icon={GitBranch}
            title="From data model"
            description="Pick tables and joins"
            active={false}
            onClick={() => setMode("model")}
          />
        </div>
      )}
      <DatasourceEditor organisationId={organisationId} mode="create" />
    </div>
  );
}

function ModeCard({
  icon: Icon,
  title,
  description,
  active,
  onClick,
}: {
  icon: typeof Database;
  title: string;
  description: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "flex flex-col items-start gap-1 rounded-lg border p-4 text-left transition-colors",
        active ? "border-primary bg-primary/5" : "hover:bg-muted/50",
      )}
    >
      <Icon className="h-5 w-5 text-muted-foreground" />
      <span className="font-medium">{title}</span>
      <span className="text-xs text-muted-foreground">{description}</span>
    </button>
  );
}
